import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { fmtBkk } from '../../utils/bkkTime';
import { menuPill, pageToolbar, rowTint, PAGE_INSET } from '../../tabPalette';
import { HtmlFrame, fileIcon } from '../EmailDetail';
import Pagination from '../Pagination';

// ── Emails waiting on a supervisor's check (report 4aaa018b) ───────────────────────────────────────────────
// What the Approvals button in the Email box opens: the emails this login was asked to check (server: services/emailCheck.ts), the
// whole of each one, with Approve and Send back. Approve sends it exactly as written, from the writer's mailbox; nothing can be
// edited here, because what was read must be what goes. Send back returns it to the writer's Drafts with the note. It stays apart
// from the Outbox tab.
const HOME = 'email_check';
type Row = {
  id: string; accountEmail: string; accountName: string; writer: string; to: string[]; cc: string[]; bcc: string[]; subject: string;
  attachments: { id: string; name: string; type: string; size: number }[]; bodyLang: string; targetLang: string; askedAt: number; bodyHtml?: string;
};
const fmtBytes = (b: number) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

function Reader({ row, onDone }: { row: Row; onDone: () => void }) {
  const { t } = useTranslation();
  const { uiLang, showToast } = useStore();
  const [full, setFull] = useState<Row | null>(null);
  const [gone, setGone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [noting, setNoting] = useState(false);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    let dead = false;
    api.emailCheck(row.id)
      .then((d: any) => { if (dead) return; if (d?.ok) setFull(d.row); else setGone(true); })
      .catch((e: any) => { if (!dead) setErr(e?.message || t('ec_failed')); });
    return () => { dead = true; };
  }, [row.id, t]);

  const answer = async (call: () => Promise<{ ok: boolean; error?: string }>, done: string) => {
    setBusy(true); setErr('');
    try {
      const r = await call();
      if (r.ok === false) { setErr(r.error || t('ec_failed')); setBusy(false); return; }
      showToast(done);
      onDone();
    } catch (e: any) { setErr(e?.message || t('ec_failed')); setBusy(false); }
  };
  const download = async (att: { id: string; name: string }) => {
    const blob = await api.emailCheckFile(row.id, att.id).catch(() => null);
    if (!blob) { setErr(t('ec_failed')); return; }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = att.name;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const head = (label: string, value: string) => (
    <div style={{ display: 'flex', gap: 8, fontSize: 12.5, lineHeight: '18px' }}>
      <span style={{ width: 62, flexShrink: 0, color: 'var(--text-tertiary)', fontWeight: 600 }}>{label}</span>
      <span style={{ minWidth: 0, overflowWrap: 'anywhere', color: 'var(--text-primary)' }}>{value}</span>
    </div>
  );
  if (gone) return <div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: 'var(--text-tertiary)' }}>{t('ec_gone')}</div>;
  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <div style={{ padding: `12px ${PAGE_INSET}px`, borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4, overflowWrap: 'anywhere' }}>{row.subject || '(No subject)'}</div>
          {head(t('ec_from'), row.accountName ? `${row.accountName} <${row.accountEmail}>` : row.accountEmail)}
          {head(t('to'), row.to.join(', ') || '—')}
          {row.cc.length > 0 && head(t('ec_cc'), row.cc.join(', '))}
          {row.bcc.length > 0 && head(t('ec_bcc'), row.bcc.join(', '))}
          {head(t('ec_writer'), `${row.writer || '—'} · ${fmtBkk(row.askedAt * 1000, uiLang)}`)}
          {row.targetLang && row.targetLang !== row.bodyLang && (
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 }}>
              <i className="ti ti-language" style={{ fontSize: 12 }} aria-hidden /> {t('ec_translated', { lang: t(`langNames.${row.targetLang}` as any, { defaultValue: row.targetLang }) })}
            </div>
          )}
          {row.attachments.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {row.attachments.map(a => (
                <button key={a.id} onClick={() => { void download(a); }} title={a.name}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 9px', background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 8, maxWidth: 260, cursor: 'pointer' }}>
                  <i className={`ti ${fileIcon(a.type || '')}`} style={{ fontSize: 14, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                  <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)', flexShrink: 0 }}>{fmtBytes(Number(a.size) || 0)}</span>
                  <i className="ti ti-download" style={{ fontSize: 13, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ background: '#fff' }}>
          {full
            ? <HtmlFrame html={full.bodyHtml || ''} />
            : <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 22, color: 'var(--text-tertiary)' }} aria-hidden /></div>}
        </div>
      </div>
      <div style={{ padding: `10px ${PAGE_INSET}px`, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
        {noting && (
          <textarea value={note} onChange={e => setNote(e.target.value)} placeholder={t('ec_note_ph')} aria-label={t('ec_note_ph')} rows={3} autoFocus
            style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', resize: 'vertical', fontFamily: 'inherit' }} />
        )}
        {err && <div style={{ fontSize: 12, color: '#B42318' }}>{err}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {noting ? (
            <>
              <button onClick={() => { void answer(() => api.returnEmailCheck(row.id, note.trim()), t('ec_returned')); }} disabled={busy || !note.trim()}
                style={{ padding: '7px 16px', borderRadius: 999, border: '1px solid rgba(226,75,74,0.45)', background: 'transparent', color: '#E24B4A', fontWeight: 700, fontSize: 12.5, cursor: busy ? 'wait' : !note.trim() ? 'not-allowed' : 'pointer', opacity: !note.trim() ? 0.55 : 1 }}>
                {t('ec_send_back')}
              </button>
              <button onClick={() => { setNoting(false); setErr(''); }} disabled={busy}
                style={{ padding: '7px 14px', borderRadius: 999, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' }}>
                {t('eml_sched_cancel')}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => { void answer(() => api.approveEmailCheck(row.id), t('ec_approved')); }} disabled={busy || !full}
                style={{ padding: '7px 18px', borderRadius: 999, border: '1px solid', fontWeight: 700, fontSize: 12.5, cursor: busy ? 'wait' : !full ? 'not-allowed' : 'pointer', opacity: !full ? 0.55 : 1, ...menuPill(HOME, true) }}>
                {t('oe_approve')}
              </button>
              <button onClick={() => setNoting(true)} disabled={busy}
                style={{ padding: '7px 16px', borderRadius: 999, border: '1px solid rgba(226,75,74,0.45)', background: 'transparent', color: '#E24B4A', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
                {t('ec_send_back')}
              </button>
            </>
          )}
          <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{noting ? t('ec_back_hint') : t('ec_approve_hint')}</span>
        </div>
      </div>
    </div>
  );
}

export default function EmailChecksView() {
  const { t } = useTranslation();
  const { uiLang } = useStore();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [picked, setPicked] = useState('');
  const load = useCallback(async (p: number) => {
    setErr('');
    try {
      const r = await api.emailChecks(p);
      setRows(r.rows || []); setTotal(Number(r.total) || 0); setPage(Number(r.page) || p); setLimit(Number(r.limit) || 100);
    } catch (e: any) { setErr(e?.message || t('ec_failed')); } finally { setLoading(false); }
  }, [t]);
  useEffect(() => { void load(1); }, [load]);
  // The one opened from the red number's list (?focus=) until another is picked, else the newest. A later row of that list wins again.
  const focus = searchParams.get('focus') || '';
  useEffect(() => { setPicked(''); }, [focus]);
  const wanted = picked || focus;
  const open = rows.find(r => r.id === wanted) || rows[0];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', ...pageToolbar(), background: 'var(--c-gray-50)', flexWrap: 'wrap' }}>
        <i className="ti ti-mail-pause" style={{ fontSize: 15, color: 'var(--text-secondary)' }} aria-hidden />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{t('appr_arrived_email_check')}</span>
        {!loading && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{total}</span>}
      </div>
      {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '10px 16px' }}>{err}</div>}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} aria-hidden /></div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: 'var(--text-tertiary)' }}>{t('ec_empty')}</div>
      ) : (
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <div style={{ width: 320, flexShrink: 0, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ flex: 1, overflowY: 'auto', background: rowTint(HOME) }}>
              {rows.map(r => (
                <button key={r.id} onClick={() => setPicked(r.id)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', borderBottom: '1px solid var(--border)', background: open?.id === r.id ? 'var(--bg-active)' : 'transparent', cursor: 'pointer' }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.subject || '(No subject)'}
                    {r.attachments.length > 0 && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-tertiary)' }}><i className="ti ti-paperclip" aria-hidden /> {r.attachments.length}</span>}
                  </span>
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{t('to')}: {r.to.join(', ') || '—'}</span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{r.writer || '—'} · {fmtBkk(r.askedAt * 1000, uiLang)}</span>
                </button>
              ))}
            </div>
            {total > limit && <Pagination page={page} total={total} limit={limit} onPage={p => { void load(p); }} />}
          </div>
          {/* A key per email: a note half typed for one never carries over to the next. */}
          {open && <Reader key={open.id} row={open} onDone={() => { setPicked(''); void load(1); }} />}
        </div>
      )}
    </div>
  );
}
